import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Save, Loader2, ChevronDown, ChevronUp, Star, UserPlus, Users, Copy, Check, Link2, ExternalLink, ClipboardCheck, ClipboardCopy } from "lucide-react";
import { toast } from "sonner";

const PLACEHOLDERS = [
  { var: "{{customer_name}}", label: "Customer" },
  { var: "{{customer_phone}}", label: "Phone" },
  { var: "{{customer_phone2}}", label: "Phone 2" },
  { var: "{{customer_email}}", label: "Email" },
  { var: "{{customer_address}}", label: "Address" },
  { var: "{{job_id}}", label: "Job ID" },
  { var: "{{job_title}}", label: "Job Title" },
  { var: "{{job_type}}", label: "Service Type" },
  { var: "{{job_date}}", label: "Date" },
  { var: "{{job_time}}", label: "Time" },
  { var: "{{schedule_time_range}}", label: "Time Range" },
  { var: "{{job_status}}", label: "Status" },
  { var: "{{job_address}}", label: "Full Address" },
  { var: "{{job_price}}", label: "Price" },
  { var: "{{technician_name}}", label: "Tech Name" },
  { var: "{{business_name}}", label: "Business" },
  { var: "{{business_phone}}", label: "Biz Phone" },
  { var: "{{review_link}}", label: "Review Link" },
  { var: "{{payment_link}}", label: "Pay Link" },
  { var: "{{invoice_amount}}", label: "Invoice Amt" },
  { var: "{{assignment_link}}", label: "Assignment" },
];

const INVITE_PLACEHOLDERS = [
  { var: "{name}", label: "Name" },
  { var: "{business_name}", label: "Business" },
  { var: "{invite_link}", label: "Invite Link" },
];

const JOB_APPROVAL_PLACEHOLDERS = [
  { var: "{{technician_name}}", label: "Tech Name" },
  { var: "{{customer_name}}", label: "Customer" },
  { var: "{{customer_address}}", label: "Address" },
  { var: "{{job_city}}", label: "City" },
  { var: "{{job_zip}}", label: "ZIP" },
  { var: "{{job_id}}", label: "Job ID" },
  { var: "{{job_title}}", label: "Job Title" },
  { var: "{{job_type}}", label: "Service Type" },
  { var: "{{job_date}}", label: "Date" },
  { var: "{{job_time}}", label: "Time" },
  { var: "{{schedule_time_range}}", label: "Time Range" },
  { var: "{{job_price}}", label: "Price" },
  { var: "{{job_notes}}", label: "Notes" },
  { var: "{{assignment_link}}", label: "Accept/Reject Link" },
  { var: "{{business_name}}", label: "Business" },
  { var: "{{business_phone}}", label: "Biz Phone" },
];

const COPY_TICKET_PLACEHOLDERS = [
  { var: "{{business_name}}", label: "Business" },
  { var: "{{job_id}}", label: "Job ID" },
  { var: "{{job_title}}", label: "Job Title" },
  { var: "{{customer_name}}", label: "Customer" },
  { var: "{{customer_phone}}", label: "Phone" },
  { var: "{{customer_phone2}}", label: "Phone 2" },
  { var: "{{customer_address}}", label: "Address" },
  { var: "{{job_date}}", label: "Date" },
  { var: "{{job_time}}", label: "Time" },
  { var: "{{schedule_time_range}}", label: "Time Range" },
  { var: "{{job_type}}", label: "Service Type" },
  { var: "{{vehicle}}", label: "Vehicle" },
  { var: "{{job_notes}}", label: "Notes" },
  { var: "{{masked_call_number_with_pin}}", label: "Masked Call #" },
  { var: "{{masked_call_number2_with_pin}}", label: "Masked Call # 2" },
];

const APPROVAL_PAGE_PLACEHOLDERS = [
  { var: "{{business_name}}", label: "Business" },
  { var: "{{job_id}}", label: "Job ID" },
  { var: "{{job_title}}", label: "Job Title" },
  { var: "{{customer_name}}", label: "Customer" },
  { var: "{{masked_call_number_with_pin}}", label: "Masked Call #" },
  { var: "{{masked_call_number2_with_pin}}", label: "Masked Call # 2" },
  { var: "{{customer_phone}}", label: "Phone" },
  { var: "{{customer_phone2}}", label: "Phone 2" },
  { var: "{{customer_address}}", label: "Address" },
  { var: "{{job_date}}", label: "Date" },
  { var: "{{job_time}}", label: "Time" },
  { var: "{{schedule_time_range}}", label: "Time Range" },
  { var: "{{job_type}}", label: "Service Type" },
  { var: "{{vehicle}}", label: "Vehicle" },
  { var: "{{job_notes}}", label: "Notes" },
  { var: "{{job_price}}", label: "Price" },
];

const InviteTemplatesTab = () => {
  const [templates, setTemplates] = useState({
    technician_sms: "",
    customer_portal_sms: "",
    customer_portal_email_subject: "",
    review_request_sms: "",
    job_approval_sms: "",
    copy_ticket_template: "",
    job_approval_page_template: "",
  });
  const [googleReviewUrl, setGoogleReviewUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingUrl, setSavingUrl] = useState(false);

  const getAuthConfig = () => {
    const token = localStorage.getItem("token");
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [templatesRes, businessRes] = await Promise.all([
          axios.get(`${API}/settings/invite-templates`, getAuthConfig()),
          axios.get(`${API}/settings/business`, getAuthConfig()),
        ]);
        setTemplates({
          technician_sms: templatesRes.data.technician_sms || "",
          customer_portal_sms: templatesRes.data.customer_portal_sms || "",
          customer_portal_email_subject: templatesRes.data.customer_portal_email_subject || "",
          review_request_sms: templatesRes.data.review_request_sms || "",
          job_approval_sms: templatesRes.data.job_approval_sms || "",
          copy_ticket_template: templatesRes.data.copy_ticket_template || "",
          job_approval_page_template: templatesRes.data.job_approval_page_template || "",
        });
        setGoogleReviewUrl(businessRes.data.google_review_url || "");
      } catch (err) {
        console.error("Failed to load templates", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/invite-templates`, templates, getAuthConfig());
      toast.success("Templates saved");
    } catch (err) {
      toast.error("Failed to save templates");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveReviewUrl = async () => {
    setSavingUrl(true);
    try {
      await axios.put(`${API}/settings/business`, { google_review_url: googleReviewUrl }, getAuthConfig());
      toast.success("Google Review URL saved");
    } catch (err) {
      toast.error("Failed to save URL");
    } finally {
      setSavingUrl(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invite-templates-tab">
      {/* Review Request Template */}
      <Card className="glass-card border-theme" data-testid="review-request-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground font-['Manrope'] flex items-center gap-2 text-base">
            <Star className="w-5 h-5 text-[#FFCC00]" /> Review Request SMS
          </CardTitle>
          <CardDescription className="text-muted-foreground text-sm">
            Sent to customers when you request a review from a completed job.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Google Review URL */}
          <div className="space-y-2">
            <Label className="text-foreground font-medium text-sm flex items-center gap-1.5">
              <Link2 className="w-3.5 h-3.5 text-blue-500" /> Google Reviews URL
            </Label>
            <div className="flex gap-2">
              <input
                type="url"
                value={googleReviewUrl}
                onChange={(e) => setGoogleReviewUrl(e.target.value)}
                placeholder="https://g.page/r/your-business/review"
                className="flex-1 px-3 py-2 rounded-lg bg-theme-surface border border-theme text-foreground text-sm placeholder-muted-foreground focus:border-[#FFCC00]/50 focus:outline-none"
                data-testid="google-review-url-input"
              />
              <Button
                onClick={handleSaveReviewUrl} disabled={savingUrl}
                size="sm" variant="outline" className="border-blue-500/30 text-blue-600 dark:text-blue-400 hover:bg-blue-500/10 shrink-0"
                data-testid="save-review-url-btn"
              >
                {savingUrl ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              </Button>
            </div>
            <p className="text-muted-foreground text-xs">
              This URL fills the <code className="bg-theme-hover px-1 rounded text-[10px]">{"{{review_link}}"}</code> placeholder.{" "}
              <a href="https://support.google.com/business/answer/7035772" target="_blank" rel="noopener noreferrer"
                className="text-blue-500 hover:underline inline-flex items-center gap-0.5">
                How to find your link <ExternalLink className="w-2.5 h-2.5" />
              </a>
            </p>
          </div>

          <TemplateEditor
            value={templates.review_request_sms}
            onChange={(v) => setTemplates({ ...templates, review_request_sms: v })}
            placeholders={PLACEHOLDERS}
            testId="template-review-request"
          />
        </CardContent>
      </Card>

      {/* Job Approval Template */}
      <Card className="glass-card border-theme" data-testid="job-approval-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground font-['Manrope'] flex items-center gap-2 text-base">
            <ClipboardCheck className="w-5 h-5 text-blue-400" /> Job Approval SMS
          </CardTitle>
          <CardDescription className="text-muted-foreground text-sm">
            Sent to technicians when a job is assigned for approval (accept/reject).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <TemplateEditor
            value={templates.job_approval_sms}
            onChange={(v) => setTemplates({ ...templates, job_approval_sms: v })}
            placeholders={JOB_APPROVAL_PLACEHOLDERS}
            testId="template-job-approval"
          />
        </CardContent>
      </Card>

      {/* Technician Approval Page Template */}
      <Card className="glass-card border-theme" data-testid="approval-page-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground font-['Manrope'] flex items-center gap-2 text-base">
            <ClipboardCheck className="w-5 h-5 text-violet-400" /> Technician Approval Page
          </CardTitle>
          <CardDescription className="text-muted-foreground text-sm">
            Job details shown on the page after a technician accepts a job (the accept/reject link page).
            Lines with empty values are hidden automatically. Phone placeholders respect call masking.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <TemplateEditor
            value={templates.job_approval_page_template}
            onChange={(v) => setTemplates({ ...templates, job_approval_page_template: v })}
            placeholders={APPROVAL_PAGE_PLACEHOLDERS}
            testId="template-approval-page"
          />
        </CardContent>
      </Card>

      {/* Copy Ticket Template */}
      <Card className="glass-card border-theme" data-testid="copy-ticket-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground font-['Manrope'] flex items-center gap-2 text-base">
            <ClipboardCopy className="w-5 h-5 text-emerald-400" /> Copy Ticket Template
          </CardTitle>
          <CardDescription className="text-muted-foreground text-sm">
            Format of the ticket text copied to clipboard after a technician accepts a job.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <TemplateEditor
            value={templates.copy_ticket_template}
            onChange={(v) => setTemplates({ ...templates, copy_ticket_template: v })}
            placeholders={COPY_TICKET_PLACEHOLDERS}
            testId="template-copy-ticket"
          />
        </CardContent>
      </Card>

      {/* Invite Templates */}
      <Card className="glass-card border-theme" data-testid="invite-templates-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground font-['Manrope'] flex items-center gap-2 text-base">
            <UserPlus className="w-5 h-5 text-green-400" /> Invite Message Templates
          </CardTitle>
          <CardDescription className="text-muted-foreground text-sm">
            Customize messages sent when inviting technicians and customers.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label className="text-foreground font-medium text-sm flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-cyan-500" /> Technician Invite SMS
            </Label>
            <TemplateEditor
              value={templates.technician_sms}
              onChange={(v) => setTemplates({ ...templates, technician_sms: v })}
              placeholders={INVITE_PLACEHOLDERS}
              testId="template-technician-sms"
            />
            <p className="text-xs text-muted-foreground">Sent when you invite a technician to set up their account.</p>
          </div>

          <div className="space-y-2">
            <Label className="text-foreground font-medium text-sm flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-purple-500" /> Customer Portal Invite SMS
            </Label>
            <TemplateEditor
              value={templates.customer_portal_sms}
              onChange={(v) => setTemplates({ ...templates, customer_portal_sms: v })}
              placeholders={INVITE_PLACEHOLDERS}
              testId="template-customer-sms"
            />
            <p className="text-xs text-muted-foreground">Sent when you invite a customer to the Customer Portal.</p>
          </div>

          <div className="space-y-2">
            <Label className="text-foreground font-medium text-sm">Customer Portal Email Subject</Label>
            <input
              type="text"
              value={templates.customer_portal_email_subject}
              onChange={(e) => setTemplates({ ...templates, customer_portal_email_subject: e.target.value })}
              placeholder="{business_name} invites you to their Customer Portal"
              className="w-full px-3 py-2 rounded-lg bg-theme-surface border border-theme text-foreground text-sm"
              data-testid="template-customer-email-subject"
            />
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="bg-[#FFCC00] text-black hover:bg-[#FFCC00]/90 font-semibold w-full sm:w-auto" data-testid="save-templates-btn">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
        Save All Templates
      </Button>
    </div>
  );
};


/** Template editor with inline clickable placeholder pills */
const TemplateEditor = ({ value, onChange, placeholders, testId, defaultOpen = false }) => {
  const textareaRef = useRef(null);
  const [showPlaceholders, setShowPlaceholders] = useState(defaultOpen);
  const [copiedVar, setCopiedVar] = useState(null);

  const insertPlaceholder = (varStr) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      onChange((value || "") + varStr);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = (value || "").substring(0, start);
    const after = (value || "").substring(end);
    const newValue = before + varStr + after;
    onChange(newValue);
    // Restore cursor after the inserted placeholder
    requestAnimationFrame(() => {
      textarea.focus();
      const newPos = start + varStr.length;
      textarea.setSelectionRange(newPos, newPos);
    });
    setCopiedVar(varStr);
    setTimeout(() => setCopiedVar(null), 1200);
  };

  return (
    <div className="space-y-2">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-theme-surface border-theme text-foreground min-h-[90px] text-sm font-mono"
        data-testid={testId}
      />
      {/* Placeholder toggle — compact on mobile */}
      <div>
        <button
          type="button"
          onClick={() => setShowPlaceholders(!showPlaceholders)}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-theme-hover"
          data-testid={`${testId}-toggle-placeholders`}
        >
          {showPlaceholders ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {showPlaceholders ? "Hide" : "Insert"} Placeholders
        </button>
        {showPlaceholders && (
          <div className="mt-1.5 flex flex-wrap gap-1 max-h-[120px] overflow-y-auto p-2 rounded-lg border border-theme bg-theme-surface/50">
            {placeholders.map((p) => (
              <button
                key={p.var}
                type="button"
                onClick={() => insertPlaceholder(p.var)}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border transition-all duration-150
                  ${copiedVar === p.var
                    ? "bg-green-500/15 border-green-500/40 text-green-600 dark:text-green-400"
                    : "bg-theme-surface border-theme text-muted-foreground hover:text-foreground hover:border-[#FFCC00]/40 hover:bg-[#FFCC00]/5"
                  }`}
                title={p.var}
              >
                {copiedVar === p.var ? <Check className="w-2.5 h-2.5" /> : <Copy className="w-2.5 h-2.5 opacity-50" />}
                {p.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteTemplatesTab;
